import { Injectable } from '@angular/core';
import { LMFormatUtilService } from '@services/utils/lm-format-util.service';
import { AppConstants, LMTimeStampDirective, LMTransformOptions, LMToolbarActionTypeEnum } from '@utils/app.constants';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMFormatterService } from '@list/formatters/lm-formatter.service';
import { LMErrorUtilService } from '@services/utils/lm-error-util.service';
import { DomSanitizer } from '@angular/platform-browser';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMProcessIndicatorService } from '../../field/processindicator/lm-process-indicator.service';
import { LMUrlUtilService } from '@app/services';

@Injectable()
export class LMListCardService {
  // todo would like provide services w/o state to app.component. Can't do that here because
  // LMFormatterService does have state. And it cannot be provided at a lower level than
  // the LMListCardService

  private _cardWidth: number;

  // Keep track of heights for each section
  // Use this to set section to the largest height
  private sectionedCardHeightMap = new Map();
  private sectionedCardLineHeight = 20;

  // add extra stuff into the templateField object so it doesn't have to be computed later
  // when the template is processing. Set default font size to match lm-font-md size
  private defaultFieldStyle: LMFieldStyle =  { header: 3 };
  private defaultTextStyle: LMTextStyle = { header: 3 };

  constructor(
    private domSanitizer: DomSanitizer,
    private lmConditionUtilService: LMConditionUtilService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmFormatterService: LMFormatterService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmThemeService: LMThemeService,
    private lmProcessIndicatorService: LMProcessIndicatorService,
    private lmUrlUtilService: LMUrlUtilService
  ) {}

  public getCardTemplateData(displayField: LMListDisplayField, dataView: LMDataView): LMCardTemplateData {
    try {
      const buttonRow:  Array<LMToolbarButton> = this.getCardButtonRowButtons(displayField.buttonRow, dataView);
      const foregroundText = this.getImageForegroundText(displayField.leftColumn, dataView);
      const cardTemplate: LMCardTemplateData = {
        imageUrl: this.getLeftColumnImageUrl(displayField.leftColumn, dataView),
        isMissingImage: this.getIsMissingImage(displayField.leftColumn, dataView),
        imageForegroundText: foregroundText,
        isRandomBackground: this.getIsRandomBackgroundImage(displayField.leftColumn, dataView),
        cardImageClasses: this.getCardImageClasses(displayField),
        bgColorClass: this.getBackgroundColorClass(displayField.leftColumn, dataView, foregroundText),
        columns : [
          { column: this.getDisplayFieldValues(displayField.mainColumn, dataView, displayField.showLabels) },
          { column: this.getDisplayFieldValues(displayField.rightColumn, dataView, displayField.showLabels) }
        ],
        buttonRow
      };

      return cardTemplate;

    } catch (e) {
      this.lmErrorUtilService.handleError(e.message, e.stack);
    }
  }

  public getSectionedCardData(sectionedCard: LMSectionedCard, dataView: LMDataView): Array<LMSectionedCardTemplate> {
    const data: Array<LMSectionedCardTemplate> = [];

    sectionedCard.sections.forEach((section, index) => {

      if (section.imageSection) {
        const imageSection = section.imageSection;
        const tag = this.getTagTemplate(section.imageSection, dataView);

        const sectionTemplate: LMSectionedCardTemplate = {
          sectionType: 'image',
          section: {
            imageUrl: this.getImageSectionImageUrl(imageSection, dataView),
            lines: imageSection.lines,
            tag
          },
          sectionHeight: imageSection.lines * this.sectionedCardLineHeight
        };

        data.push(sectionTemplate);
      }

      if (section.titleSection) {
        const titleSection = section.titleSection;
        const sectionTemplate: LMSectionedCardTemplate = {
          sectionType: 'title',
          section: {
            displayValue: this.lmFormatUtilService.formatMessageRule(titleSection.title, dataView),
            lines: titleSection.lines
          },
          sectionHeight: titleSection.lines * this.sectionedCardLineHeight
        };

        if (titleSection.navigation) {
          sectionTemplate.section.navigationParams = { navigationName: titleSection.navigation, dataView };
        }

        data.push(sectionTemplate);
      }

      if (section.rowSection) {
        const fields = [];

        section.rowSection.fields.forEach((field) => {
          if (field.navigation) {
            const action = field as LMFormButtonSpec;
            const actions = this.getActionsForSection([action], dataView);
            actions.leftSection.forEach((a) => fields.push(a));
            actions.rightSection.forEach((a) => fields.push(a));
          } else {
            fields.push(this.getDisplayFieldValues([field], dataView, true)[0]);
          }
        });

        const sectionTemplate: LMSectionedCardTemplate = {
          sectionType: 'row',
          section: { fields }
        };

        this.updateSectionHeightMap(sectionTemplate.section.fields, index);
        sectionTemplate.sectionHeight = this.sectionedCardHeightMap.get(`section-${index}`).height;

        data.push(sectionTemplate);
      }

      if (section.fieldSection) {
        const sectionTemplate: LMSectionedCardTemplate = {
          sectionType: 'field',
          section: {
            fields: this.getDisplayFieldValues(section.fieldSection.fields, dataView, true)
          }
        };

        this.updateSectionHeightMap(sectionTemplate.section.fields, index);
        sectionTemplate.sectionHeight = this.sectionedCardHeightMap.get(`section-${index}`).height;

        data.push(sectionTemplate);
      }

      if (section.actionSection) {
        const actions = this.getActionsForSection(section.actionSection.actions, dataView);
        data.push({
          sectionType: 'action',
          section: {
            actions
          }
        });
      }
    });

    return data;
  }

  public get cardWidth(): number {
    return this._cardWidth;
  }

  public set cardWidth(width: number) {
    this._cardWidth = width;
  }

  private getBackgroundColorClass(leftColumn: LMCardViewLeftColumn, dataView: LMDataView, foregroundText: string) {
    if (leftColumn === undefined || !this.getIsMissingImage(leftColumn, dataView)) {
      return null;
    }

    return `image-initials ${this.lmThemeService.getUniqueBackgroundColorClass(foregroundText)}`;
  }

  private getIsRandomBackgroundImage(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): boolean {
    if (leftColumn === undefined || !this.getIsMissingImage(leftColumn, dataView)) {
      return null;
    }

    return leftColumn.isRandomBackground;
  }

  private getIsMissingImage(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): boolean {
    if (leftColumn === undefined || dataView === undefined) {
      return null;
    }

    return dataView.fields[leftColumn.fieldId] ? dataView.fields[leftColumn.fieldId].missingImage : null;
  }

  private getImageForegroundText(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): string {
    if (leftColumn === undefined || leftColumn.foregroundText === undefined) {
      return null;
    } else if (leftColumn.foregroundText) {
      // Display a max of 4 characters
      const text = this.lmFormatUtilService.formatMessageRule(leftColumn.foregroundText, dataView);
      const chars = text.split('').filter((char, index) => index <= 3 );
      return chars.join('');
    }

    return null;
  }

  private getCardImageClasses(displayFields: LMListDisplayField): string {
    const classes = [ 'lm-card-image' ];

    if (displayFields.leftColumn && displayFields.leftColumn.displayImageAs) {
      const orientationClass = 'lm-' + displayFields.leftColumn.displayImageAs + '-image';
      classes.push(orientationClass);
    }

    return classes.join(' ');
  }

  private getImageSectionImageUrl(imageSection: LMSectionedCardImageSection, dataView: LMDataView): string {
    const url = dataView.fields[imageSection.fieldId].value as string;
    const height = imageSection.lines * this.sectionedCardLineHeight;
    const size = `${this.cardWidth}x${height}`;
    return this.lmUrlUtilService.updateUrlParams(url, { size });
  }

  private getLeftColumnImageUrl(leftColumn: LMCardViewLeftColumn, dataView: LMDataView): string {
    if (leftColumn === undefined || dataView === undefined) {
      return null;
    }

    // Use random background image and not the missing image url
    if (leftColumn.isRandomBackground
      && leftColumn.foregroundText
      && (dataView.fields[leftColumn.fieldId] && dataView.fields[leftColumn.fieldId].missingImage)
    ) {
      return null;
    }

    if (leftColumn.fieldId && dataView.fields[leftColumn.fieldId]) {
      return String(dataView.fields[leftColumn.fieldId].value);
    } else if (leftColumn.fieldId === undefined && dataView.representativeImageUrl) {
      return dataView.representativeImageUrl;
    } else {
      return '';
    }
  }

  private getDisplayFieldValues(
    displayFields: Array<LMListDisplayField>,
    dataView: LMDataView,
    showLabels?: boolean
  ): Array<LMListViewTemplateField> {

    const values: Array<LMListViewTemplateField> = [];

    if (displayFields === undefined || dataView === undefined) {
      return values;
    }

    displayFields.forEach((displayField: LMListDisplayField) => {
      const field: LMField = dataView.fields[this.getFieldID(displayField)];
      const label: string = displayField.label && !displayField.labelSuppressed ? this.lmFormatUtilService.formatMessageRule(displayField.label, dataView) : '';
      const isBlankLine = displayField.type === undefined && displayField.isBlankLine;
      const val = field ? field.value : '';
      const displayFieldType = isBlankLine ? 'BLANKLINE' : displayField.type.toUpperCase();
      const transformOptions: LMTransformOptions = {};

      if (displayField.isBlankLine) {
        values.push({ type: displayFieldType, displayValue: '' });
        return;
      }

      if (displayField.visibility && !this.lmConditionUtilService.isValid(displayField.visibility, dataView)) {
        return;
      }

      if (displayFieldType === AppConstants.BCD && field.precision) {
        transformOptions.precision = (displayField.numberOfDecimals || displayField.numberOfDecimals === 0) ? displayField.numberOfDecimals : field.precision;
      }

      if (displayFieldType === AppConstants.BCD && displayField.decSize) {
        transformOptions.decSize = (displayField.numberOfDecimals || displayField.numberOfDecimals === 0) ? displayField.numberOfDecimals : displayField.decSize;
      }

      if (displayFieldType === AppConstants.BCD && displayField.showPercentSymbol) {
        transformOptions.showPercentSymbol = displayField.showPercentSymbol;
      }

      if (displayFieldType === AppConstants.BCD && displayField.currencySymbolInfo) {
        transformOptions.currencySymbolIs = this.lmFormatUtilService.getCurrencySymbol(displayField.currencySymbolInfo, dataView);
      }

      if ((displayFieldType === AppConstants.BCD && displayFieldType === AppConstants.BCD) && displayField.displayNegativeAmountAs === 'parens') {
        transformOptions.displayNegativeParens = displayField.displayNegativeAmountAs;
      }

      if (displayField?.timeZoneIs) {
        transformOptions.timeZone = dataView?.fields[displayField.timeZoneIs].value as string;
      }

      if (displayField?.displayWithTimeZone) {
        transformOptions.displayWithTimeZone = true;
      }

      if (displayFieldType === AppConstants.TIMESTAMP && (displayField?.displayDateAndHoursAndMinutes || displayField?.limitToMinuteIncrements)) {
        transformOptions.timeStampDirective = LMTimeStampDirective.DISPLAY_DATE_AND_HOURS_AND_MINUTES;
      }

      if (displayFieldType === AppConstants.TIMESTAMP && displayField?.disableDST) {
        transformOptions.disableDST = true;
      }

      const displayValue = this.getFieldDisplayValue(displayFieldType, String(val), displayField, dataView, transformOptions);
      const displayAsTag = this.lmConditionUtilService.displayAsTag(displayField, dataView, displayValue);
      const lines = displayField.lines ? displayField.lines : displayField.showChars ? this.showCharsToLines(displayField.showChars) : 1;

      const templateField: LMListViewTemplateField = {
        fieldId: displayField.fieldId,
        type: displayFieldType,
        label: showLabels ? label : null,
        navigationParams: displayField.linkNavigation ? { navigationName: displayField.linkNavigation, dataView } : null,
        alertCondition: this.getAlertCondition(displayField, dataView),
        httpUrl: displayField.httpUrl ? this.getMsgRuleValue(displayField.httpUrl, dataView) : null,
        value: this.getFieldValue(displayFieldType, String(val), undefined, transformOptions),
        openExternal: displayField.openExternal ? displayField.openExternal : null,
        displayValue,
        displayAsTag,
        icon: displayField.icon ? this.getIcon(displayField) : null,
        lines,
        height: lines * this.sectionedCardLineHeight
      };

      if (displayField.displayAsProgressBar) {
        templateField.progressFieldData = <LMProgressFieldData>{
          value: this.getFieldValue(displayFieldType, Number(val), displayField.meterRangeValue),
          displayValue: templateField.displayValue,
          meterRangeValue: displayField.meterRangeValue,
          ranges: displayField.ranges,
          dataView,
          field,
          displayPercent: AppConstants.PERCENT === displayField.type
        };
      }

      if (displayField.displayAsRating) {
        templateField.ratingOptions = this.lmFormatUtilService.formatStarRatingOptions(val as number, displayField.id);
        templateField.displayAsRating = true;
      }

      templateField.isField = this.isField(templateField, displayField);
      templateField.isFormText = (templateField.type === AppConstants.FORMTEXT);
      templateField.isInternalNavigation = this.isInternalNavigation(templateField);
      templateField.isExternalNavigation = this.isExternalNavigation(templateField);
      templateField.isProcessIndicator = this.isProcessIndicator(displayField);

      if (templateField.isProcessIndicator) {
        const value = dataView.fields[displayField.fieldId].value;

        if (value === '') {
          return;
        }

        const processIndicatorData = JSON.parse(value.toString());
        templateField.processIndicatorDisplayData = this.getProcessIndicatorData(<LMProcessIndicatorData>processIndicatorData);
      }

      if (displayAsTag) {
        if (displayField.tagStyle && displayField.tagStyle.length > 0) {
          templateField.fieldStyle = this.getFieldStyle(displayField, dataView);
        }
      } else {
        templateField.fieldStyle = { ...this.defaultFieldStyle, ...displayField.fieldStyle };
      }

      templateField.textStyle = { ...this.defaultTextStyle, ...displayField.textStyle };
      templateField.isRichText = displayField.isRichText;

      values.push(templateField);
    });

    return values;
  }

  private getFieldStyle(field: LMListDisplayField, dataView): LMFieldStyle {
    const tagStyle = field.tagStyle.find(item => {
      if (!item.condition) {
        return true;
      }

      return this.lmConditionUtilService.isValid([ item.condition ], dataView);
    });

    return  { ...this.defaultFieldStyle, ...field.fieldStyle, ...tagStyle.style };
  }

  private getTagTemplate(field: LMSectionedCardImageSection, dataView): LMSectionedCardTagTemplate {
    const tagTemplate: LMSectionedCardTagTemplate = { isValid: false, style: {} };

    field.tags?.find(item => {
      if (item.colors) {
        item.colors.forEach((tagColor) => {
          const colorIsValid = this.lmConditionUtilService.isValid([ tagColor.condition ], dataView);

          // Set color based on condition
          if (colorIsValid) {
            tagTemplate.style.color = tagColor.color as LMStyleColor;
          }

          const tag = dataView.fields[tagColor.condition.fieldId];
          tagTemplate.tagValue = tag ? this.lmFormatUtilService.booleanToDisplay(tag.value) : null;
        });

        // Set tag visibility
        if (item.condition) {
          tagTemplate.isValid = this.lmConditionUtilService.isValid([ item.condition ], dataView);
        }
      }
    });

    return tagTemplate;
  }

  private isField(templateField: LMListViewTemplateField, displayField: LMListDisplayField) {
    return (templateField.displayValue.trim().length > 0
      && !displayField.isProcessIndicator
      && !displayField.httpUrl
      && !templateField.navigationParams
      && !templateField.progressFieldData);
  }

  private isInternalNavigation(item: LMListViewTemplateField): boolean {
    if (
      item === undefined ||
      item.value === '' ||
      item.navigationParams === undefined ||
      item.navigationParams === null
    ) {
      return false;
    }

    return true;
  }

  private isProcessIndicator(displayField: LMListDisplayField): boolean {
    return displayField.isProcessIndicator && (displayField.type === AppConstants.TEXTAREA || displayField.type === AppConstants.TEXT);
  }

  private isExternalNavigation(item: LMListViewTemplateField): boolean {
    if (
      item === undefined ||
      (item.httpUrl === undefined || item.httpUrl === null) ||
      (item.httpUrl.trim().length === 0)
    ) {
      return false;
    } else if (item.httpUrl) {
      return true;
    }
  }

  private getFieldID(displayField: LMListDisplayField): string {
    if (displayField.fieldId !== undefined) {
      return displayField.fieldId;
    } else {
      return this.getFieldIdFromVariables(displayField);
    }
  }

  private getFieldIdFromVariables(displayField: LMListDisplayField): string {
    let fieldId: string;

    for (const key in displayField) {
      if (
        displayField.hasOwnProperty(key) &&
        displayField[key].variables
      ) {
        fieldId = displayField[key].variables[0].fieldId;
      }
    }

    return fieldId !== undefined ? fieldId : '';
  }

  private getIcon(displayField: LMListDisplayField): LMListIcon {

    const temp: LMListIcon = {
      icon: displayField.icon,
      iconUrl: displayField.icon ? displayField.iconUrl  : '',
      sohoIcon: displayField.icon ? this.lmThemeService.isValidSohoIcon(displayField.icon) : false,
    };

    return temp;
  }

  private getFieldValue(
    type: string,
    val: string | number,
    meterRangeValue?: number,
    transformOptions?: LMTransformOptions
  ): string | number {
    const value = {
      [AppConstants.PERCENT]    : () => this.convertValueToPercent(Number(val), meterRangeValue),
      [AppConstants.NUMERIC]    : () => this.lmFormatUtilService.numericToData(val).toString(),
      [AppConstants.BCD]        : () => this.lmFormatUtilService.bcdToData(String(val), transformOptions),
      [AppConstants.TIMESTAMP]  : () => this.lmFormatUtilService.timestampToData(val, transformOptions),
      [AppConstants.YYYYMMDD]   : () => this.lmFormatUtilService.yyyymmddToData(val),
      [AppConstants.TIME]       : () => this.lmFormatUtilService.timeToData(val),
      [AppConstants.YYYYMM]     : () => this.lmFormatUtilService.yyyymmToData(val),
      [AppConstants.MMDD]       : () => this.lmFormatUtilService.mmddToData(val),
      [AppConstants.YYYY]       : () => this.lmFormatUtilService.yyyyToData(val),
      default                 : () => val
    };

    return (value[type] || value['default'])();
  }

  private convertValueToPercent(value: number, meterRangeValue: number): number {
    const range: number = meterRangeValue || 1.0;
    const num = Number( ((value / range) * 100).toFixed(2) );
    return num > 100 ? 100 : num;
  }

  private getFieldDisplayValue(
    type: string,
    val: string,
    displayField: LMListDisplayField,
    dataView: LMDataView,
    transformOptions?: LMTransformOptions
  ): string {
    // Angular sanitizes the value in span
    // val = this.domSanitizer.sanitize(SecurityContext.HTML, val);
    const fieldId = this.getFieldID(displayField);
    const format = this.lmFormatUtilService;

    let states;

    if (displayField.states) {
      states = displayField.states;
    }

    if (displayField.isUserDefinedStateField) {
      states = dataView.fields[fieldId].stateValues;
    }

    const value = {
      [AppConstants.TIMESTAMP]  : () => format.timestampToDisplay(val, transformOptions),
      [AppConstants.YYYYMMDD]   : () => format.yyyymmddToDisplay(val),
      [AppConstants.TIME]       : () => format.timeToDisplay(val),
      [AppConstants.YYYYMM]     : () => format.yyyymmToDisplay(val),
      [AppConstants.MMDD]       : () => format.mmddToDisplay(val),
      [AppConstants.YYYY]       : () => format.yyyyToDisplay(val),
      [AppConstants.PERCENT]    : () => states ? format.stateLabelToText(states, val) : format.percentToDisplay(val),
      [AppConstants.NUMERIC]    : () => states ? format.stateLabelToText(states, val) : format.numericToDisplay(val),
      [AppConstants.BCD]        : () => states ? format.stateLabelToText(states, val) : format.bcdToDisplay(val, transformOptions),
      [AppConstants.BOOLEAN]    : () => states ? format.stateLabelToText(states, val) : format.booleanToDisplay(val),
      [AppConstants.FORMTEXT]   : () => displayField.text ? this.getMsgRuleValue(displayField.text, dataView) : val,
      default                 : () => states ? format.stateLabelToText(states, val) : val
    };

    return (value[type] || value['default'])();
  }

  private getMsgRuleValue(messageRule: LMMessageRule | string, dataView: LMDataView, isHttpMessageRule?: boolean) {
    if (isHttpMessageRule) {
      return this.lmFormatUtilService.formatMessageRule(messageRule, dataView).replace('mailto:', '');
    } else {
      return this.lmFormatUtilService.formatMessageRule(messageRule, dataView);
    }
  }

  private getAlertCondition(displayField: LMListDisplayField, dataView: LMDataView): LMAlertCondition {
    let alertCondition: LMAlertCondition;

    if (displayField.alert) {
      const condition = this.lmFormatterService.getAlertCondition(displayField, dataView);
      alertCondition = condition && condition.icon !== undefined ? condition : null;
    }

    return alertCondition;
  }

  private getCardButtonRowButtons(
    buttonSpecs: Array<LMFormButtonSpec>,
    dataView: LMDataView,
  ): Array<LMToolbarButton> {

    const buttons: Array<LMToolbarButton> = [];

    if (buttonSpecs === undefined || dataView === undefined) {
      return buttons;
    }

    buttonSpecs.forEach((formButtonSpec) => {
      const t = formButtonSpec.type.toUpperCase();

      if (
        (formButtonSpec.condition && !this.lmConditionUtilService.isValid([ formButtonSpec.condition ], dataView)) ||
        (formButtonSpec.visibility && !this.lmConditionUtilService.isValid(formButtonSpec.visibility, dataView))
      ) {
        return;
      }

      // DPB adding cardViewFormButtonSpec to handle busform button pushes from a list -- Phillip thinking of better architecture solution.
      const label = formButtonSpec.title ? this.lmFormatUtilService.formatMessageRule(formButtonSpec.title, dataView) : '';
      const httpUrl = this.getHttpUrl(formButtonSpec, dataView);
      const button: LMCardViewButtonRowButton = {
        actionReference: {
          id: formButtonSpec.fieldId,
          index: formButtonSpec.actionSpec ? formButtonSpec.actionSpec.index : null,
          row: dataView.id - 1,
          type: formButtonSpec.type,
          label,
          validity: [ formButtonSpec.condition ],
          navigation: formButtonSpec.navigation,
          resourceId: dataView.resourceId,
          cardViewFormButtonSpec: formButtonSpec,
          tooltip: this.getToolTip(formButtonSpec, label)
        },
        httpUrl,
        isOpenExternal: formButtonSpec.openExternal,
        actionSpec: formButtonSpec.actionSpec,
        isDisplayAsLink: formButtonSpec.displayAsLink && !formButtonSpec.iconUrl,
        isIconOnly: formButtonSpec.iconUrl !== undefined,
        iconUrl: formButtonSpec.iconUrl,
        icon: this.lmThemeService.isValidSohoIcon(formButtonSpec.icon) ? formButtonSpec.icon : null,
        state: { disabled: false, visible: true },
        fieldStyle: formButtonSpec.fieldStyle,
        isPrimary: formButtonSpec.isPrimary,
        displayTooltip: label === ' ' ? false :  true
      };

      switch (t) {
        case AppConstants.FORMBUTTON :
          buttons.push(button);
          break;
        case AppConstants.NAVIGATION :
          if (formButtonSpec.navigation !== undefined) {
            button.navigationParams = { navigationName: formButtonSpec.navigation, dataView };
          }
          buttons.push(button);
          break;
      }
    });

    return buttons;
  }

  private getToolTip(formButtonSpec: LMFormButtonSpec, label: string): string {
    const tooltip = formButtonSpec.iconUrl !== undefined && label.trim().length !== 0 ? label : null;
    const httpUrl = formButtonSpec.httpUrl ? formButtonSpec.httpUrl.resourceValue : null;
    if (this.isMailToLink(httpUrl)) {
      // TODO: Localize
      return 'Send Email';
    }
    return tooltip;
  }

  private isMailToLink(value: string): boolean {
    if (!value) {
      return false;
    }

    return value.includes('mailto:');
  }

  private getHttpUrl(formButtonSpec: LMFormButtonSpec, dataView: LMDataView): string {
    let url = null;
    if (formButtonSpec.hasHttpUrl) {
      url = this.lmFormatUtilService.formatMessageRule(formButtonSpec.httpUrl, dataView);
    }

    return url;
  }

  private getProcessIndicatorData(processIndicatorData: LMProcessIndicatorData): Array<LMProcessIndicatorDisplayData> {
    return this.lmProcessIndicatorService.getTemplateData(processIndicatorData);
  }

  public encodeHtmlSingleQuote(button: LMCardViewButtonRowButton): LMCardViewButtonRowButton{
    const buttonCopy = button;

    if(button.actionReference?.cardViewFormButtonSpec?.title?.resourceValue) {
      buttonCopy.actionReference.cardViewFormButtonSpec.title.resourceValue = button.actionReference.cardViewFormButtonSpec.title.resourceValue.replace('\'', '&#39;');
    }

    return buttonCopy;
  }

  private showCharsToLines(size: number): number {
    return Math.ceil(size / 50);
  }

  private updateSectionHeightMap(fields: Array<LMListViewTemplateField|LMToolbarButton>, sectionIndex) {
    let sectionHeight = 0;
    fields.forEach((field) => {
      const _field = field as LMListViewTemplateField;
      sectionHeight+= _field.height;
    });

    const existingSection = this.sectionedCardHeightMap.get(`section-${sectionIndex}`);

    if (existingSection) {
      this.sectionedCardHeightMap.set(`section-${sectionIndex}`, { height: existingSection.height > sectionHeight ? existingSection.height : sectionHeight });
    } else {
      this.sectionedCardHeightMap.set(`section-${sectionIndex}`, { height: sectionHeight });
    }
  }

  private getActionsForSection(actions: Array<LMCheckControl|LMFormButtonSpec>, dataView: LMDataView): LMSectionedCardActionSectionActions {
    const _actions = {
      leftSection: [],
      rightSection: []
    };

    actions.forEach((action: LMFormButtonSpec|LMCheckControl) => {
      // @ts-ignore
      if (Object.values(LMToolbarActionTypeEnum).includes(action.type)) {
        const buttonSpec = action as LMFormButtonSpec;
        const buttonSpecs = this.getCardButtonRowButtons([ buttonSpec ], dataView);

        // Card button rows could return empty array if action is not valid
        if (buttonSpecs.length > 0) {
          _actions.rightSection.push(buttonSpecs[0]);
        }
      }

      if (action.type === AppConstants.CHECKCONTROL) {
        _actions.leftSection.push(action);
      }
    });

    return _actions;
  }
}
